import { computed, effect, inject, untracked } from '@angular/core';

import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { AppError, SpinnerService, WINDOW } from '@hglobal/fin-ui';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { interval, map, switchMap } from 'rxjs';

import { Account, ACCOUNT_GROUP_TYPE, AccountType, CardStatus, Currency, DEFAULT_CURRENCY } from '@zeusm/personal/core/entities';
import { stringToInteger } from '@zeusm/personal/core/helpers';
import { DictionariesStore } from '@zeusm/personal/store';
import { getUserCodeConfigs } from '@zeusm/shared/core/entities';
import { AuthStore } from '@zeusm/shared/session/auth';
import { ProfileService } from '@zeusm/shared/session/profile';
import {
  setErrorStatusFor,
  setPendingStatusFor,
  setSuccessStatusFor,
  StoreHelperService,
  withRequestHandler,
  withRequestStatus,
} from '@zeusm/shared/store';

import { SHELL_API } from './api';
import { GetAccountsRes } from './models';
import { HIDE_BALANCE_STORAGE_KEY } from './shell.constants';

interface PersonalShellState {
  accounts: Account[];
  balanceHidden: boolean;
  fiatAccounts: Account[];
  cryptoAccounts: Account[];
  cardAccounts: Account[];
  operationCompleted: string;
  treasurerAccounts: Account[];
}

const initialState: PersonalShellState = {
  accounts: [],
  fiatAccounts: [],
  cryptoAccounts: [],
  cardAccounts: [],
  balanceHidden: false,
  treasurerAccounts: [],
  operationCompleted: null,
};

const TRANSITION_CARD_POLL_INTERVAL = 30e3;
const SHOULD_POLL_CARD_STATUSES = new Set([
  CardStatus.Creating,
  CardStatus.Activating,
  CardStatus.Blocking,
  CardStatus.Closing,
  CardStatus.Suspending,
]);

export const PersonalShellStore = signalStore(
  withDevtools('personal-shell'),
  withState(initialState),
  withRequestStatus(['getAccounts']),
  withRequestHandler(),
  withProps(store => ({
    store,
    shellApi: inject(SHELL_API),
    authStore: inject(AuthStore),
    window: inject<Window>(WINDOW),
    spinnerService: inject(SpinnerService),
    dictionariesStore: inject(DictionariesStore),
    profileService: inject(ProfileService),
    storeHelperService: inject(StoreHelperService),
  })),
  withComputed(({ profileService }) => ({
    defaultCurrency: computed(() =>
      profileService.me()?.salaryCurrency && !profileService.isActiveTreasurer() ? profileService.me().salaryCurrency : DEFAULT_CURRENCY,
    ),
    isCodeEnabled: computed(() => getUserCodeConfigs(profileService.me()).length > 0),
  })),
  withMethods(({ store, window }) => ({
    handleOperationCompleted: (operationId: string) => patchState(store, { operationCompleted: operationId }),
    clearOperationCompleted: () => patchState(store, { operationCompleted: null }),
    setBalanceHidden: (value: boolean) => {
      if (!value) {
        window.localStorage.removeItem(HIDE_BALANCE_STORAGE_KEY);
      } else {
        window.localStorage.setItem(HIDE_BALANCE_STORAGE_KEY, 'true');
      }
      patchState(store, { balanceHidden: value });
    },
  })),
  withMethods(({ store, shellApi, dictionariesStore, clearOperationCompleted, defaultCurrency }) => ({
    getAccounts: rxMethod<boolean | void>(
      switchMap(isPolling => {
        const polling = isPolling === true;
        patchState(store, setPendingStatusFor('getAccounts'));
        return shellApi.getAccounts(polling).pipe(
          map(accountsData => accountsDataWithCurrency(accountsData, dictionariesStore.currencies())),
          map(accountsData => accountsDataWithAccountType(accountsData)),
          tapResponse({
            next: accounts => {
              patchState(
                store,
                {
                  accounts: [...accounts.fiatAccounts, ...accounts.cryptoAccounts, ...accounts.cardAccounts],
                  fiatAccounts: accounts?.fiatAccounts,
                  cryptoAccounts: accounts?.cryptoAccounts,
                  cardAccounts: accounts?.cardAccounts,
                },
                setSuccessStatusFor('getAccounts'),
              );
              clearOperationCompleted();
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('getAccounts', error)),
          }),
        );
      }),
    ),
    clearAll: () => {
      patchState(store, {
        accounts: [],
        fiatAccounts: [],
        cryptoAccounts: [],
        cardAccounts: [],
      });
    },
    calculateBalanceInDefaultCurrency: (account: Account): string => {
      const accCurr = account.currency;
      const defaultCurr = defaultCurrency();

      const rateFromCurrentCurrencyToDefault = dictionariesStore.getRate(accCurr.symbol, defaultCurr);
      if (!rateFromCurrentCurrencyToDefault) {
        return '';
      }

      const balanceInDefaultCurr = stringToInteger(account.balance) * stringToInteger(rateFromCurrentCurrencyToDefault);
      return balanceInDefaultCurr.toFixed(accCurr.decimals).toString();
    },
  })),
  withMethods(({ store, dictionariesStore, calculateBalanceInDefaultCurrency }) => ({
    addBalanceInDefaultCurrency: () => {
      if (!store.accounts()?.length || !dictionariesStore.rates()?.length || !dictionariesStore.currencies()?.length) {
        return;
      }

      const accounts = store.accounts().map(account => ({
        ...account,
        balanceInDefaultCurr: calculateBalanceInDefaultCurrency(account),
      }));
      const fiatAccounts = store.fiatAccounts().map(account => ({
        ...account,
        balanceInDefaultCurr: calculateBalanceInDefaultCurrency(account),
      }));
      const cryptoAccounts = store.cryptoAccounts().map(account => ({
        ...account,
        balanceInDefaultCurr: calculateBalanceInDefaultCurrency(account),
      }));

      patchState(store, { accounts, fiatAccounts, cryptoAccounts });
    },
  })),
  withHooks({
    onInit(store) {
      if (!store.profileService.me().hasPersonalProfileAccess) return;

      const balanceHidden = store.window.localStorage.getItem(HIDE_BALANCE_STORAGE_KEY);
      patchState(store, { balanceHidden: Boolean(balanceHidden) });

      store.dictionariesStore.getCurrencies();
      store.dictionariesStore.getRates();

      effect(() => {
        store.profileService.profileChange(); // for refetch on profile change
        const status = store.dictionariesStore.isGetCurrenciesSuccess() && store.authStore.isAuthorized();
        if (status) {
          untracked(() => store.getAccounts());
        }
      });

      effect(() => {
        store.dictionariesStore.isGetRatesSuccess();
        store.isGetAccountsSuccess();

        untracked(() => store.addBalanceInDefaultCurrency());
      });

      effect(() => {
        if (store.operationCompleted()) {
          untracked(() => store.getAccounts());
        }
      });

      effect(onCleanup => {
        const shouldPoll = store.cardAccounts().some(item => SHOULD_POLL_CARD_STATUSES.has(item.card?.status));
        if (!shouldPoll) return;

        const sub = interval(TRANSITION_CARD_POLL_INTERVAL).subscribe(() => {
          store.getAccounts(true);
        });

        onCleanup(() => sub.unsubscribe());
      });

      effect(() => {
        const isAuthorized = store.authStore.isAuthorized();
        if (!isAuthorized) {
          untracked(() => store.clearAll());
        }
      });

      effect(() => {
        if (store.error()) {
          untracked(() => store.storeHelperService.handleError(store.error()));
        }
      });
    },
  }),
);

export function mapAccountWithType(accounts: Account[], accountType: AccountType): Account[] {
  return accounts?.map(account => ({
    ...account,
    accountType: account.type === ACCOUNT_GROUP_TYPE.CLIENT ? accountType : AccountType.TreasurerWallet,
  }));
}

export function accountsDataWithAccountType(accountsRes: GetAccountsRes | null | undefined): GetAccountsRes | null {
  if (!accountsRes) {
    return null;
  }

  return {
    fiatAccounts: mapAccountWithType(accountsRes.fiatAccounts, AccountType.FiatWallet),
    cryptoAccounts: mapAccountWithType(accountsRes.cryptoAccounts, AccountType.CryptoWallet),
    cardAccounts: mapAccountWithType(accountsRes.cardAccounts, AccountType.BankCard),
  };
}

export function accountsDataWithCurrency(
  accountsRes: GetAccountsRes | null | undefined,
  currencies: Currency[] | null | undefined,
): GetAccountsRes | null {
  if (!accountsRes || !currencies?.length) {
    return null;
  }

  const currencyMap = new Map(currencies.map(c => [c.id, c]));

  const attachCurrency = (account: Account): Account => {
    const currency = currencyMap.get(account.currencyId) ?? null;

    return {
      ...account,
      currency,
    };
  };
  return {
    fiatAccounts: accountsRes.fiatAccounts?.map(attachCurrency) ?? [],
    cryptoAccounts: accountsRes.cryptoAccounts?.map(attachCurrency) ?? [],
    cardAccounts: accountsRes.cardAccounts?.map(attachCurrency) ?? [],
  };
}
